import type { UserPreferences } from './User';

// Every preferences write sends the whole content, so one built while another is unanswered can land after it and
// undo it. These keep the order: the writes sent, the newest version the store was given, and the keys whose write is
// unanswered or whose removal is not yet written, which the store already shows.

/** Nothing times a request out, so a key's write waits this long for the writes before it, then gives up. */
export const PREFERENCES_SETTLE_LIMIT_MS = 10000;
export const PREFERENCES_STILL_SAVING = 'Your earlier changes are still being saved. Please try again.';

let writesSent: Promise<void> = Promise.resolve();
let unanswered = 0;
let newestAt = Number.NEGATIVE_INFINITY;
const unansweredKeys = new Map<string, unknown>();

/** Sends a write so that a key's write can wait for it; it counts as answered once its answer is in the store. */
export const trackPreferencesWrite = <T>(write: () => Promise<T>): Promise<T> => {
  // Counted before it starts, so its own answer sees it; the async call settles one that throws before it is sent.
  unanswered += 1;
  const written = (async () => write())();
  const answered = () => {
    unanswered -= 1;
  };
  written.then(answered, answered);
  // Keeps the order only: holding each answer would keep every copy of the preferences for as long as the page is open.
  writesSent = Promise.allSettled([writesSent, written]).then(() => undefined);
  return written;
};

/** Whether a write sent so far is still unanswered; read just before a write, it holds until that write is sent. */
export const preferencesWritesUnanswered = (): boolean => unanswered > 0;

/** Resolves true once the writes sent so far are answered, or false if `deadline` (a `Date.now()` time) comes first. */
export const preferencesWritesSettledBy = (deadline: number): Promise<boolean> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), Math.max(deadline - Date.now(), 0));
    writesSent.then(() => {
      clearTimeout(timer);
      resolve(true);
    });
  });

const updatedAtOf = (preferences: UserPreferences): number => Date.parse(preferences.updated_at ?? '');

/** Notes the version of preferences the store is given, from the `updated_at` the API sends with them. */
export const notePreferencesVersion = (preferences: UserPreferences): void => {
  const at = updatedAtOf(preferences);
  if (!Number.isNaN(at)) newestAt = Math.max(newestAt, at);
};

/** Whether an answer is older than what the store holds, and would move it back, dropping what was written since. */
export const isOlderThanStore = (preferences: UserPreferences): boolean => updatedAtOf(preferences) < newestAt;

/** Whether a broadcast is no newer than what the store holds; one can arrive after the answer to a later write. */
export const isNoNewerThanStore = (preferences: UserPreferences): boolean => updatedAtOf(preferences) <= newestAt;

/** A copy of the preferences with one content key set to `value`, or removed when `value` is undefined. */
export const withContentKey = (preferences: UserPreferences, key: string, value: unknown): UserPreferences => {
  const copy: UserPreferences = JSON.parse(JSON.stringify(preferences));
  const content = copy.content as unknown as Record<string, unknown>;
  if (value === undefined) delete content[key];
  else content[key] = value;
  return copy;
};

export const markKeyUnanswered = (key: string, value: unknown): void => {
  unansweredKeys.set(key, value);
};

export const markKeyAnswered = (key: string): void => {
  unansweredKeys.delete(key);
};

/** Whether a key's removal waits for an answer that shows it written, so the store keeps the key out until then. */
export const isRemovalPending = (key: string): boolean =>
  unansweredKeys.has(key) && unansweredKeys.get(key) === undefined;

/**
 * Forgets the removals an answer shows written. It is called within the write it answers, which still counts, and
 * does nothing while another write is unanswered: that one could still land after it and bring a removed key back.
 */
export const notePersistedRemovals = (preferences: UserPreferences): void => {
  if (unanswered > 1) return;
  const content = preferences.content as unknown as Record<string, unknown>;
  unansweredKeys.forEach((value, key) => {
    if (value === undefined && content[key] === undefined) unansweredKeys.delete(key);
  });
};

/** The preferences with every unanswered key and pending removal laid over them, as the store already shows those. */
export const withUnansweredKeys = (preferences: UserPreferences): UserPreferences =>
  [...unansweredKeys].reduce((laid, [key, value]) => withContentKey(laid, key, value), preferences);

/** Forgets the versions and keys of a user who signed out; the writes sent still count until they are answered. */
export const resetPreferencesWrites = (): void => {
  newestAt = Number.NEGATIVE_INFINITY;
  unansweredKeys.clear();
};
